import { useEffect, useRef } from "react";

/**
 * useBackToClose — el botón atrás (del teléfono o del navegador) cierra el
 * panel abierto en vez de sacar al usuario de la pantalla.
 *
 * Abrir empuja UNA entrada de historial con la misma URL y el mismo state del
 * router (más una marca), así React Router no ve navegación. Atrás la saca y
 * cierra todo panel abierto; cerrar desde la UI (✕, scrim, Escape) la deshace
 * con `history.back()`, para que el historial quede como si no se hubiera
 * abierto nada.
 *
 * La entrada es compartida por todos los paneles: pasar de la ficha a Editar
 * cierra uno y abre otro en el mismo render, y con una entrada por panel el
 * `back()` asíncrono del primero se llevaba la del segundo. Por eso el back se
 * difiere un tick: si otro panel abrió entretanto, la entrada se reusa.
 */
const MARK = "__slidePanel";
const open = new Set();
let pushed = false;
let poppingByUs = false;
let listening = false;

const isOurs = () => window.history.state?.[MARK] === true;

function push() {
  window.history.pushState({ ...window.history.state, [MARK]: true }, "");
  pushed = true;
}

function onPopState() {
  if (poppingByUs) {
    poppingByUs = false;
    return;
  }
  if (!pushed || isOurs()) return;
  // El usuario tocó atrás: la entrada ya no está.
  pushed = false;
  for (const ref of [...open]) ref.current?.();
  // Un onClose que no cierra (p. ej. pide confirmar descartar cambios) deja
  // el panel abierto: se repone la entrada para que el próximo atrás también
  // pase por él.
  setTimeout(() => {
    if (open.size && !pushed) push();
  }, 0);
}

export function useBackToClose(isOpen, onClose) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return undefined;
    if (!listening) {
      window.addEventListener("popstate", onPopState);
      listening = true;
    }
    open.add(onCloseRef);
    if (!pushed) push();
    return () => {
      open.delete(onCloseRef);
      setTimeout(() => {
        // Otro panel tomó la entrada, o ya se fue (atrás, o una navegación
        // desde el panel empujó otra ruta encima: no hay que deshacer nada).
        if (open.size || !pushed) return;
        pushed = false;
        if (!isOurs()) return;
        poppingByUs = true;
        window.history.back();
      }, 0);
    };
  }, [isOpen]);
}
